import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaPlus, FaPencilAlt } from 'react-icons/fa';
import { createApiClient } from '../../api/client';
import { useCaseContext } from '../context/CaseContext';
import { getPreferredLang, SECTION_INFO_I18N, CONTINUE_PROMPT_I18N } from '../../utils/sectionConfirmations';
import '../mobile.css';

const MobileAccusedList = ({ API_URL }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { fullFIR, setFullFIR } = useCaseContext();
  // Overrides location.state.fir once a name (or other) update is saved, so
  // this screen reflects it immediately without needing a fresh navigation -
  // location.state.fir would otherwise keep shadowing the updated fullFIR.
  const [localFir, setLocalFir] = useState(null);
  const fir = localFir || location.state?.fir || fullFIR;
  const accusedList = fir?.accusedList || [];
  const isStatement = Boolean(location.state?.statement);

  const [loadingName, setLoadingName] = useState(null);
  const [error, setError] = useState(null);
  const [confirmAccused, setConfirmAccused] = useState(null);

  const [namePopup, setNamePopup] = useState(null); // { accused }
  const [newNameInput, setNewNameInput] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const [nameError, setNameError] = useState('');

  const openNamePopup = (accused) => {
    setNamePopup({ accused });
    setNewNameInput(accused.name || '');
    setNameError('');
  };

  const closeNamePopup = () => {
    setNamePopup(null);
    setNewNameInput('');
    setNameError('');
  };

  const saveName = async () => {
    if (!namePopup || !newNameInput.trim()) return;

    setIsSavingName(true);
    setNameError('');

    try {
      const api = createApiClient(API_URL);
      const response = await api.post(`/fir/name/${fir.id}`, {
        user: 'Accused',
        existingName: namePopup.accused.name,
        newName: newNameInput.trim(),
      });
      setFullFIR(response.data);
      setLocalFir(response.data);
      closeNamePopup();
    } catch {
      setNameError('Failed to update the name. Please try again.');
    } finally {
      setIsSavingName(false);
    }
  };

  const generateQuestions = async (accused) => {
    setConfirmAccused(null);
    setLoadingName(accused.name);
    setError(null);

    try {
      const api = createApiClient(API_URL);
      const response = await api.post(`/fir/accused/iq/${fir.id}`, { name: accused.name });
      setFullFIR(response.data);
      const updated = (response.data.accusedList || []).find((a) => a.name === accused.name);
      navigate('/m/fir/witnesses/questions', {
        state: { id: fir.id, from: 'Accused', name: accused.name, interrogationQuestions: updated?.interrogationQuestions || [] },
      });
    } catch {
      setError('Failed to generate interrogation questions. Please try again.');
    } finally {
      setLoadingName(null);
    }
  };

  const openQuestions = (accused) => {
    if (Array.isArray(accused.interrogationQuestions) && accused.interrogationQuestions.length > 0) {
      navigate('/m/fir/witnesses/questions', {
        state: { id: fir.id, from: 'Accused', name: accused.name, interrogationQuestions: accused.interrogationQuestions },
      });
      return;
    }

    setConfirmAccused(accused);
  };

  if (!fir) return <p style={{ textAlign: 'center', fontWeight: 700 }}>No case data available.</p>;

  if (accusedList.length === 0) {
    return (
      <div style={{ position: 'relative', minHeight: '60vh' }}>
        <p style={{ textAlign: 'center', fontWeight: 700 }}>No accused persons identified yet.</p>
        {!isStatement && (
          <button
            onClick={() => navigate('/m/fir/accused/new', { state: { fir } })}
            className="m-chat-fab"
            style={{ right: '30px', bottom: 'calc(130px + env(safe-area-inset-bottom, 0px))', background: '#094268' }}
            aria-label="Add accused"
          >
            <FaPlus size={22} color="#fff" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', minHeight: '60vh' }}>
      {accusedList.map((accused, index) => (
        <div key={accused.name || index} className="m-card">
          <div style={{ fontSize: '16px', fontWeight: 700, color: '#111', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{accused.name}</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); openNamePopup(accused); }}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex' }}
              aria-label="Edit name"
              title="Edit name"
            >
              <FaPencilAlt size={13} color="#6b7280" />
            </button>
          </div>
          <div style={{ marginTop: '4px', textAlign: 'justify' }}>{accused.relationshipWithComplainant}</div>
          <div style={{ marginTop: '4px', color: '#666', textAlign: 'justify' }}>Source: {accused.source || 'FIR'}</div>

          <div style={{ marginTop: '10px' }}>
            <button
              onClick={() => openQuestions(accused)}
              disabled={loadingName === accused.name}
              className="m-button-primary"
              style={{ width: 'auto', padding: '8px 14px', fontSize: '13px' }}
            >
              {loadingName === accused.name ? <div className="m-spinner" style={{ margin: '0 auto' }} /> : 'Interrogation Questions'}
            </button>
          </div>
        </div>
      ))}

      {!isStatement && (
        <button
          onClick={() => navigate('/m/fir/accused/new', { state: { fir } })}
          className="m-chat-fab"
          style={{ right: '30px', bottom: 'calc(130px + env(safe-area-inset-bottom, 0px))', background: '#094268' }}
          aria-label="Add accused"
        >
          <FaPlus size={22} color="#fff" />
        </button>
      )}

      {confirmAccused && (
        <div className="m-popup-overlay">
          <div className="m-popup-container">
            <h3>{(SECTION_INFO_I18N[getPreferredLang()] || SECTION_INFO_I18N.en).interrogations.title}</h3>
            <p style={{ textAlign: 'justify' }}>
              {(SECTION_INFO_I18N[getPreferredLang()] || SECTION_INFO_I18N.en).interrogations.text}
              <br /><br />
              {CONTINUE_PROMPT_I18N[getPreferredLang()] || CONTINUE_PROMPT_I18N.en}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setConfirmAccused(null)} className="m-pill-btn" style={{ color: '#094268', fontWeight: 600 }}>Cancel</button>
              <button className="m-button-primary" style={{ width: 'auto', padding: '8px 14px' }} onClick={() => generateQuestions(confirmAccused)}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {namePopup && (
        <div className="m-popup-overlay">
          <div className="m-popup-container">
            <h3>Update Name</h3>
            <input
              type="text"
              value={newNameInput}
              onChange={(e) => setNewNameInput(e.target.value)}
              placeholder="Enter the updated name"
              style={{ width: '100%', boxSizing: 'border-box', borderWidth: 1, borderStyle: 'solid', borderColor: '#ccc', borderRadius: '8px', padding: '12px', marginTop: '10px', marginBottom: '12px' }}
            />
            {nameError && <p style={{ color: '#c9302c' }}>{nameError}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={closeNamePopup} className="m-pill-btn" style={{ color: '#094268', fontWeight: 600 }} disabled={isSavingName}>Cancel</button>
              <button className="m-button-primary" style={{ width: 'auto', padding: '8px 14px' }} onClick={saveName} disabled={isSavingName || !newNameInput.trim()}>
                {isSavingName ? <div className="m-spinner" style={{ margin: '0 auto' }} /> : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="m-popup-overlay">
          <div className="m-popup-container">
            <h3>Error</h3>
            <p>{error}</p>
            <button className="m-button-primary" onClick={() => setError(null)}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileAccusedList;
